import * as THREE from '@phdsoft/three';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { RetargetPointType } from './inspection-retarget.util';

/**
 * Pure helpers that pick the point shape of an inspection loaded from the backend
 * from its stored data (Scope + TargetGeometryTypeId + ElementInfo), without the
 * target element. Used for orphan points (target not found) and for the
 * stiffener/bracket decision of targeted web/flange points.
 */

/** Shape selection for an orphan point: `type` is the `createPlatePointFromPosition` type, `null` = unknown scope. */
export interface OrphanPointShape {
  type: RetargetPointType | null;
  scope: string;
}

const STIFFENER_ID = (ELEMENTSTYPESIDS.STIFFENER as string).toLowerCase();

/**
 * True when a stored `TargetGeometryTypeId` means Stiffener: the element-type GUID
 * (as written by insertion, dashed or not, any case) or the legacy name `'Stiffener'`.
 */
export function isStiffenerTypeId(id: string | ELEMENTSTYPESIDS | null | undefined): boolean {
  if (id == null) return false;
  const value = String(id).trim().toLowerCase();
  if (!value) return false;
  return value === 'stiffener' || value.replace(/-/g, '') === STIFFENER_ID;
}

/** Point type + scope for an orphan point, from its stored `Scope` (and type id for web/flange). */
export function orphanPointShapeFor(elementResult: Pick<ElementResultInfo, 'Scope' | 'TargetGeometryTypeId'> | null | undefined): OrphanPointShape {
  const scope = elementResult?.Scope ?? '';
  switch (scope) {
    case 'ONLY PLATE':
      return { type: 'Plate', scope };
    case 'ONLY WEB':
    case 'ONLY FLANGE':
      return { type: isStiffenerTypeId(elementResult.TargetGeometryTypeId) ? 'Stiffener' : 'Bracket', scope };
    case 'ONLY FACE PLATE':
      return { type: 'FacePlate', scope };
    case 'ONLY BEAM PART':
      return { type: 'BeamPart', scope };
    case 'ONLY PIPE':
      return { type: 'Pipe', scope };
    case 'ONLY CABLE':
      return { type: 'Cable', scope };
    default:
      return { type: null, scope };
  }
}

/** Unit normal from `ElementInfo.normal`; `+Z` when missing, short, zero-length or non-finite. Always a new vector. */
export function orphanNormal(elementResult: Pick<ElementResultInfo, 'ElementInfo'> | null | undefined): THREE.Vector3 {
  const n = elementResult?.ElementInfo?.normal;
  if (Array.isArray(n) && n.length >= 3) {
    const x = Number(n[0]), y = Number(n[1]), z = Number(n[2]);
    const len = Math.sqrt(x * x + y * y + z * z);
    if (Number.isFinite(len) && len > 1e-9) return new THREE.Vector3(x / len, y / len, z / len);
  }
  return new THREE.Vector3(0, 0, 1);
}
